// EmailPreviewModal — shown after an invoice is approved, and reopenable
// later from a stored approval. Approving generates an approval email as a
// *draft for the manager to copy and send to their finance team* so the
// invoice can be paid — the app does not send it. This modal takes a bare
// `email` draft (not the approve response) so it can display either the
// just-approved response's email or the `invoice.approvalEmail` persisted on
// an already-approved invoice — the modal never recomputes the body
// (byte-parity is guaranteed backend-side). When `email.bodyHtml` is present
// the preview renders the milestone table as HTML (falling back to the plain
// `<pre>` body for legacy emails without it); Copy email always copies the
// BODY only, via the shared `copyRichText` util (text/html + text/plain
// through ClipboardItem, or a writeText fallback when the rich clipboard is
// unavailable) — the subject has its own Copy subject control since a
// "Subject: …" line was never wanted inside a pasted email body. If every
// clipboard write fails (e.g. permission denied) the feedback line shows a
// visible copy-failed message rather than doing nothing. Done dismisses.

import { useState } from "react";
import { clsx } from "clsx";
import { Copy } from "lucide-react";
import Modal from "../../common/Modal";
import Button from "../../common/Button";
import { copyRichText } from "../../../utils/richClipboard";
import type { ApprovalEmailPreview } from "../../../types/finance";

interface EmailPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceNumber: string;
  /** The stored/returned draft; null renders nothing. */
  email: ApprovalEmailPreview | null;
}

export default function EmailPreviewModal({
  isOpen,
  onClose,
  invoiceNumber,
  email,
}: EmailPreviewModalProps) {
  const [copied, setCopied] = useState<"email" | "subject" | "failed" | null>(null);

  if (!email) return null;

  // Copy the BODY only, in both flavours: Outlook pastes the text/html table,
  // plain-text targets get the text flavour. The subject has its own control
  // — pasting "Subject: …" into an email body was never wanted.
  const handleCopyEmail = async () => {
    try {
      await copyRichText(email.bodyHtml, email.body);
      setCopied("email");
    } catch {
      setCopied("failed");
    }
  };

  const handleCopySubject = async () => {
    try {
      await navigator.clipboard.writeText(email.subject);
      setCopied("subject");
    } catch {
      setCopied("failed");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Approval email"
      titleAccessory={
        <span className="text-caption text-slate-500 dark:text-slate-400">
          {invoiceNumber}
        </span>
      }
      maxWidth="sm:max-w-2xl"
    >
      <div className="space-y-4">
        <p className="text-body-sm text-slate-600 dark:text-slate-300">
          This email isn&apos;t sent automatically. Copy it and send it to your
          finance team so the invoice can be paid.
        </p>

        <dl className="space-y-2 text-body-sm">
          <div className="flex items-start gap-2">
            <dt className="w-16 shrink-0 pt-1 text-slate-500 dark:text-slate-400">
              Subject
            </dt>
            <dd className="flex flex-1 items-center justify-between gap-2 font-medium text-slate-900 dark:text-slate-100">
              <span>{email.subject}</span>
              <Button
                variant="ghost"
                size="sm"
                icon={<Copy className="h-4 w-4" aria-hidden="true" />}
                onClick={handleCopySubject}
              >
                Copy subject
              </Button>
            </dd>
          </div>
        </dl>

        <div>
          <span className="label">Message</span>
          {email.bodyHtml ? (
            // Deliberately light in both themes — the preview shows the
            // email as recipients see it.
            <div
              data-testid="email-preview-html"
              className="mt-1 max-h-80 overflow-auto rounded-lg border border-slate-200 bg-white p-3 text-slate-900 dark:border-slate-700 dark:bg-slate-100"
              // Our own generated, escaped markup — never user-supplied HTML.
              dangerouslySetInnerHTML={{ __html: email.bodyHtml }}
            />
          ) : (
            <pre className="mt-1 max-h-80 overflow-auto whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-3 font-mono text-body-sm text-slate-800 shadow-soft-inset dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:shadow-soft-inset-dark">
              {email.body}
            </pre>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 pt-1">
          <span
            aria-live="polite"
            className={clsx(
              "mr-auto text-caption",
              copied === "failed"
                ? "text-red-600 dark:text-red-400"
                : "text-emerald-700 dark:text-emerald-400",
            )}
          >
            {copied === "email"
              ? "Email copied to clipboard"
              : copied === "subject"
                ? "Subject copied to clipboard"
                : copied === "failed"
                  ? "Copy failed — check clipboard permissions"
                  : ""}
          </span>
          <Button variant="secondary" onClick={onClose}>
            Done
          </Button>
          <Button
            icon={<Copy className="h-4 w-4" aria-hidden="true" />}
            onClick={handleCopyEmail}
          >
            Copy email
          </Button>
        </div>
      </div>
    </Modal>
  );
}
